import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Building2, Search } from 'lucide-react';
import { invoicesAPI } from '../services/api';
import { useFeatureFlag } from '../context/featureFlagStore';
import { useCompanyStore } from '../context/companyStore';
import CompanyFilter from '../components/common/CompanyFilter';
import { useDisplay } from '../hooks/useDisplay';

export default function InvoicesPage() {
  const [downloadingId, setDownloadingId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const navigate = useNavigate();
  const location = useLocation();
  const lastRefreshTimestamp = useRef(null);
  const canDownloadPdf = useFeatureFlag('feature.invoice_pdf');
  const d = useDisplay();

  // Multi-company support
  const { selectedCompanyId, hasMultipleCompanies } = useCompanyStore();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['invoices', selectedCompanyId],
    queryFn: () => invoicesAPI.list({ pageSize: 50, companyId: selectedCompanyId }),
  });

  // Sync search to URL so it survives back-navigation from the detail page.
  // Detail page saves window.location.search into the return URL and
  // navigates to it — anything in URL comes back.
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [search, setSearchParams]);

  // Auto-refresh when returning from detail page.
  // Restore search from URL in case a stale in-memory value from an earlier
  // render lingers (defensive — the URL is the source of truth after nav).
  useEffect(() => {
    const refreshTimestamp = location.state?.refresh;
    if (refreshTimestamp && refreshTimestamp !== lastRefreshTimestamp.current) {
      lastRefreshTimestamp.current = refreshTimestamp;
      setSearch(searchParams.get('search') || '');
      refetch();
    }
  }, [location.state?.refresh, refetch, searchParams]);

  const allInvoices = data?.data?.invoices || [];

  // Filter by search
  const invoices = allInvoices.filter(invoice => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return invoice.invoiceNumber?.toLowerCase().includes(searchLower) ||
           invoice.company?.name?.toLowerCase().includes(searchLower);
  });

  const handleDownloadPdf = async (e, invoice) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloadingId(invoice.id);
    try {
      const response = await invoicesAPI.getPdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download invoice PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Invoices</h1>
          <p className="text-neutral-600 dark:text-neutral-400">View your billing history</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search invoices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <CompanyFilter />
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>{d.label('invoicesList.col.number')}</th>
              {hasMultipleCompanies && <th>Company</th>}
              <th>{d.label('invoicesList.col.date')}</th>
              <th>{d.label('invoicesList.col.dueDate')}</th>
              <th>{d.label('invoicesList.col.total')}</th>
              <th>{d.label('invoicesList.col.balance')}</th>
              <th>{d.label('invoicesList.col.status')}</th>
              {canDownloadPdf && <th></th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6 + (hasMultipleCompanies ? 1 : 0) + (canDownloadPdf ? 1 : 0)} className="text-center py-8 text-neutral-500 dark:text-neutral-400">Loading invoices...</td></tr>
            ) : invoices.length === 0 ? (
              <tr><td colSpan={6 + (hasMultipleCompanies ? 1 : 0) + (canDownloadPdf ? 1 : 0)} className="text-center py-8 text-neutral-500 dark:text-neutral-400">No invoices found</td></tr>
            ) : (
              invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  onClick={() => {
                    sessionStorage.setItem('invoicesReturnUrl', window.location.pathname + window.location.search);
                    navigate(`/invoices/${invoice.id}`);
                  }}
                  className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                >
                  <td className="font-medium text-primary-600 dark:text-primary-400">{invoice.invoiceNumber}</td>
                  {hasMultipleCompanies && (
                    <td>
                      <span className="inline-flex items-center gap-1 text-sm text-neutral-600 dark:text-neutral-400">
                        <Building2 className="w-3 h-3" />
                        {invoice.company?.name || 'N/A'}
                      </span>
                    </td>
                  )}
                  <td>{invoice.date ? new Date(invoice.date).toLocaleDateString() : 'N/A'}</td>
                  <td>{invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}</td>
                  <td>${(invoice.total || 0).toFixed(2)}</td>
                  <td className="font-medium">${(invoice.balance || 0).toFixed(2)}</td>
                  <td>
                    <span className={`badge ${invoice.closedFlag ? 'badge-success' : 'badge-warning'}`}>
                      {invoice.closedFlag ? 'Paid' : 'Open'}
                    </span>
                  </td>
                  {canDownloadPdf && (
                    <td>
                      <button
                        onClick={(e) => handleDownloadPdf(e, invoice)}
                        disabled={downloadingId === invoice.id}
                        className="btn-ghost btn-sm"
                        title="Download PDF"
                      >
                        <Download className={`w-4 h-4 ${downloadingId === invoice.id ? 'animate-pulse' : ''}`} />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
